import {
    ItemAddonBehaviour,
    ItemAddonType,
    type OrderApiDto,
    type OrderItemApiDto,
    type OrderItemSelectedAddonDto,
    type RestApiDeliveryMethodDto,
} from '@order-mage/integration-sdk';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type { StorefrontAddonDef } from '@/api/menu-list/storefront-menu-snapshot.types';
import { getHashFromItemIdMap, getMemberHash, resolveEffectiveAddons } from '@/entities/cart';
import { getPriceForItem } from '@/entities/cart/lib/item-price';
import type { CartFormValues } from '@/entities/cart/model/cart-context';
import type { ResolvedStorefrontItem } from '@/entities/product/lib/storefront-menu';
import { computeAddonDefaults } from '@/entities/product/ui/product-detail/compute-addon-defaults';
import { isDeliveryMethod } from '@/entities/venue/lib/isDeliveryMethod';
import { DeliveryType } from '@/features/enums/DeliveryType';
import type { Country } from '@/shared/model/Country';
import type { Currency } from '@/shared/model/Currency';
import { groupSelectionsByParent } from './group-selections';

export type BuyAgainCartItem = {
    productHash: string;
    quantity: number;
    variant?: string;
    addons?: Record<string, string | Record<string, number>>;
    /** Member item id → item_variant row id, only when that variant is not the default. */
    memberVariants?: Record<string, string>;
};

export type BuyAgainPricingContext = {
    country: Country;
    currency: Currency;
};

/**
 * True when the ordered unit price differs from the current storefront unit price (variant if any).
 * Ignores addon/package deltas on the order line — those are not re-priced here.
 */
export function isOrderItemUnitPriceChanged(
    orderItem: Pick<OrderItemApiDto, 'price'> & {
        selectedVariant?: { variantItemId?: string | null } | null;
    },
    menuItem: ResolvedStorefrontItem,
    menuItems: Record<string, ResolvedStorefrontItem>,
    pricing: BuyAgainPricingContext,
): boolean {
    const orderPrice = Number(orderItem.price);
    if (!Number.isFinite(orderPrice)) {
        return false;
    }

    const variantItemId = orderItem.selectedVariant?.variantItemId;
    const priced =
        variantItemId != null
            ? (Object.values(menuItems).find((entry) => entry.body.id === variantItemId) ?? menuItem)
            : menuItem;

    const current = getPriceForItem({ prices: priced.prices }, pricing.country, pricing.currency);
    if (!current) {
        return false;
    }

    const currentAmount = Number(current.amount);
    if (!Number.isFinite(currentAmount)) {
        return false;
    }

    return orderPrice !== currentAmount;
}

type FlatSelection = OrderItemSelectedAddonDto;

const flattenAddonDefs = (defs: StorefrontAddonDef[] | null | undefined): StorefrontAddonDef[] =>
    (defs ?? []).flatMap((addon) =>
        addon.type === ItemAddonType.GROUP && addon.groupMembers?.length ? addon.groupMembers : [addon],
    );

const findAddonDef = (defs: StorefrontAddonDef[] | null | undefined, addonId: string) => {
    const flat = flattenAddonDefs(defs);
    return (
        flat.find((def) => def.itemAddonDefinitionId === addonId) ?? flat.find((def) => def.id === addonId)
    );
};

const markDescendants = (
    id: string | null | undefined,
    children: Map<string, FlatSelection[]>,
    seen: Set<string>,
) => {
    for (const child of children.get(id ?? '') ?? []) {
        if (!child.id || seen.has(child.id)) continue;
        seen.add(child.id);
        markDescendants(child.id, children, seen);
    }
};

/**
 * Rebuilds cart addon values from flat selections. A missing branch, a removed parent,
 * a cycle, or a fourth level leaves the rest of the line in place. Prices stay on the
 * menu: the cart prices the restored quantities when it totals the line.
 */
const restoreAddonSelections = (
    selections: FlatSelection[],
    rootAddons: StorefrontAddonDef[] | null | undefined,
    menuItems: Record<string, ResolvedStorefrontItem>,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
    addons: Record<string, string | Record<string, number>>,
): { memberVariants?: Record<string, string>; unavailable: boolean } => {
    const grouped = groupSelectionsByParent(selections);
    const children = grouped.childrenOf;
    const roots = grouped.roots;
    let unavailable = grouped.dangling.length > 0;

    const seen = new Set<string>();
    const memberVariants: Record<string, string> = {};
    // A variant row's quantity is the piece count, not a delta on top of the contained amount.
    const variantPieces = new Set<string>();

    const applyList = (
        nodes: FlatSelection[],
        defs: StorefrontAddonDef[] | null | undefined,
        depth: number,
    ) => {
        const replacedSingle = new Set(
            nodes
                .filter((node) => Number(node.quantity ?? 0) > 0)
                .map((node) => node.addonId ?? node.addon?.itemAddonDefinitionId)
                .filter((id): id is string => !!id),
        );

        const apply = (selection: FlatSelection) => {
            if (depth >= MAX_ADDON_NESTING_DEPTH) {
                if (selection.id) seen.add(selection.id);
                markDescendants(selection.id, children, seen);
                return;
            }
            if (selection.id) {
                if (seen.has(selection.id)) return;
                seen.add(selection.id);
            }

            const addonId = selection.addonId ?? selection.addon?.itemAddonDefinitionId ?? undefined;
            if (!addonId || !selection.itemId) {
                unavailable = true;
                markDescendants(selection.id, children, seen);
                return;
            }
            const addonDef = findAddonDef(defs, addonId);
            const member = addonDef?.members?.find((row) => row.itemId === selection.itemId);
            const memberHash = member ? getMemberHash(member, idToHash) : undefined;
            if (!addonDef || !member || !memberHash) {
                unavailable = true;
                markDescendants(selection.id, children, seen);
                return;
            }

            const qty = Number(selection.quantity ?? 0);
            const contained = Number(member?.amount?.value ?? 0);
            if (qty < 0) {
                // A switch is +chosen and −contained. The removal must not wipe the new choice.
                if (!(addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK && replacedSingle.has(addonId))) {
                    if (addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                        addons[addonDef.id] = '';
                    } else if (!variantPieces.has(memberHash)) {
                        const current = {
                            ...((addons[addonDef.id] as Record<string, number> | undefined) ?? {}),
                        };
                        current[memberHash] = Math.max(0, contained + qty);
                        addons[addonDef.id] = current;
                    }
                }
                markDescendants(selection.id, children, seen);
                return;
            }
            if (qty > 0 || (qty === 0 && addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK)) {
                if (addonDef.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                    addons[addonDef.id] = memberHash;
                } else if (qty > 0) {
                    const current = {
                        ...((addons[addonDef.id] as Record<string, number> | undefined) ?? {}),
                    };
                    if (selection.selectedVariantId) {
                        if (!variantPieces.has(memberHash)) {
                            current[memberHash] = 0;
                            variantPieces.add(memberHash);
                        }
                        current[memberHash] = Number(current[memberHash] ?? 0) + qty;
                    } else {
                        current[memberHash] = contained + qty;
                    }
                    addons[addonDef.id] = current;
                }
            }

            let nextDefs = menuItems[memberHash]?.body.addons;
            if (selection.selectedVariantId) {
                const variant = menuItems[memberHash]?.body.variants?.find(
                    (row) => row.id === selection.selectedVariantId,
                );
                if (!variant) {
                    unavailable = true;
                    markDescendants(selection.id, children, seen);
                    return;
                }
                if (!variant.isDefault) {
                    memberVariants[selection.itemId] = variant.id;
                    const variantHash = idToHash.get(variant.itemId);
                    const variantAddons = variantHash ? menuItems[variantHash]?.body.addons : undefined;
                    if (variantAddons?.length) nextDefs = variantAddons;
                    else if (!variantHash) {
                        unavailable = true;
                        markDescendants(selection.id, children, seen);
                        return;
                    }
                }
            }

            applyList(children.get(selection.id ?? '') ?? [], nextDefs, depth + 1);
        };

        for (const node of nodes) apply(node);
    };

    applyList(roots, rootAddons, 0);
    for (const selection of selections) {
        if (selection.id && !seen.has(selection.id)) unavailable = true;
    }

    return {
        memberVariants: Object.keys(memberVariants).length > 0 ? memberVariants : undefined,
        unavailable,
    };
};

/**
 * Maps order line items onto current storefront hashes (same rules as OrderModal buy-again).
 */
export function mapOrderItemsToCartAdds(
    orderItems: OrderItemApiDto[] | null | undefined,
    menuItems: Record<string, ResolvedStorefrontItem>,
    pricing?: BuyAgainPricingContext,
): { items: BuyAgainCartItem[]; hasUnavailableItems: boolean; hasPriceChanges: boolean } {
    if (!orderItems?.length || !Object.keys(menuItems).length) {
        return {
            items: [],
            hasUnavailableItems: Boolean(orderItems?.length),
            hasPriceChanges: false,
        };
    }

    let hasUnavailableItems = false;
    let hasPriceChanges = false;
    const items: BuyAgainCartItem[] = [];
    const idToHash = getHashFromItemIdMap(menuItems);
    // One item can sit in the menu many times — as a product, and as an addon
    // option on other items. Only an orderable placement is a valid cart line.
    const menuByBodyId = new Map<string, readonly [string, ResolvedStorefrontItem]>();
    for (const [hash, menuItem] of Object.entries(menuItems)) {
        const current = menuByBodyId.get(menuItem.body.id);
        const isOrderable = menuItem.placement?.type === 'categoryItem';
        if (!current || (isOrderable && current[1].placement?.type !== 'categoryItem')) {
            menuByBodyId.set(menuItem.body.id, [hash, menuItem] as const);
        }
    }

    for (const orderItem of orderItems) {
        const menuItemEntry = menuByBodyId.get(orderItem.item.id);

        if (!menuItemEntry) {
            hasUnavailableItems = true;
            continue;
        }

        const [hash, menuItem] = menuItemEntry;

        let variantHash: string | undefined;
        const orderedVariantItemId = orderItem.selectedVariant?.variantItemId;
        if (orderedVariantItemId) {
            // The same item can be a variant here and a product elsewhere, so the
            // variant resolves under THIS parent before falling back to the map.
            variantHash =
                (menuItem.placement?.variantHashes ?? []).find((candidate) => {
                    const placement = menuItems[candidate]?.placement;
                    return (placement?.variantItemId ?? placement?.itemId) === orderedVariantItemId;
                }) ?? menuByBodyId.get(orderedVariantItemId)?.[0];

            if (!variantHash) {
                hasUnavailableItems = true;
                continue;
            }
        }

        if (pricing && isOrderItemUnitPriceChanged(orderItem, menuItem, menuItems, pricing)) {
            hasPriceChanges = true;
        }

        const effectiveAddons = resolveEffectiveAddons(menuItems, menuItem.body.addons, variantHash, hash);

        const addons: Record<string, string | Record<string, number>> = computeAddonDefaults(
            { addons: effectiveAddons ?? undefined },
            menuItems,
        );

        const restored = orderItem.selectedAddons
            ? restoreAddonSelections(orderItem.selectedAddons, effectiveAddons, menuItems, idToHash, addons)
            : undefined;
        if (restored?.unavailable) hasUnavailableItems = true;

        items.push({
            productHash: hash,
            quantity: Number(orderItem.quantity),
            variant: variantHash,
            addons: Object.keys(addons).length > 0 ? addons : undefined,
            ...(restored?.memberVariants ? { memberVariants: restored.memberVariants } : {}),
        });
    }

    return { items, hasUnavailableItems, hasPriceChanges };
}

type OrderCheckoutSource = Pick<
    OrderApiDto,
    | 'deliveryMethodId'
    | 'deliveryMethod'
    | 'payments'
    | 'address'
    | 'addressId'
    | 'contactInfo'
    | 'personalInfo'
    | 'note'
>;

/**
 * Restores delivery / payment / contact fields when those methods still exist.
 * Marks `hasUnavailableCheckout` when the ordered delivery or payment method is gone.
 */
export function mapOrderCheckoutToCartPatch(
    order: OrderCheckoutSource,
    availableDeliveryMethods: RestApiDeliveryMethodDto[] | null | undefined,
    availablePaymentMethodIds: Iterable<string>,
): { patch: Partial<CartFormValues>; hasUnavailableCheckout: boolean } {
    const patch: Partial<CartFormValues> = {};
    let hasUnavailableCheckout = false;

    const deliveryMethods = availableDeliveryMethods ?? [];
    const paymentIds = new Set(availablePaymentMethodIds);

    const method = deliveryMethods.find((m) => m.id === order.deliveryMethodId);
    if (method) {
        const type = isDeliveryMethod(method) ? DeliveryType.DELIVERY : DeliveryType.PICKUP;

        const address = order.address
            ? {
                  city: order.address.city ?? '',
                  country: String(order.address.country ?? ''),
                  postCode: order.address.postCode ?? '',
                  street: order.address.street ?? '',
                  lat: order.address.lat ?? '',
                  lon: order.address.lon ?? '',
              }
            : null;

        patch.deliveryMethod = {
            id: order.deliveryMethodId,
            type,
            address: type === DeliveryType.DELIVERY ? address : null,
            addressId: type === DeliveryType.DELIVERY ? (order.addressId ?? null) : null,
            manual: false,
        };
    } else if (order.deliveryMethodId) {
        hasUnavailableCheckout = true;
    }

    const paymentMethodId = order.payments?.[0]?.paymentMethod?.id;
    if (paymentMethodId && paymentIds.has(paymentMethodId)) {
        patch.paymentMethod = { id: paymentMethodId };
    } else if (paymentMethodId) {
        hasUnavailableCheckout = true;
    }

    const telephonePrefix = order.contactInfo?.telephonePrefix?.trim();
    const telephone = order.contactInfo?.telephone?.trim();
    const telephoneCombined =
        telephonePrefix && telephone ? `${telephonePrefix} ${telephone}` : telephone ? telephone : '';

    patch.contactInfo = {
        name: order.personalInfo?.name ?? '',
        surname: order.personalInfo?.surname ?? '',
        telephone: telephoneCombined,
        telephoneVerified: false,
        email: order.contactInfo?.email ?? '',
    };

    if (order.note != null) {
        patch.note = order.note;
    }

    return { patch, hasUnavailableCheckout };
}
